import { Routes } from '@angular/router';
import { AppLayoutComponent } from '@shared/layout/app-layout/app-layout.component';
import { authGuard } from '@shared/auth/guards/auth.guard';
import { redirectAuthenticatedGuard } from '@shared/auth/guards/redirect-authenticated.guard';
import { adminGuard } from '@shared/auth/guards/admin.guard';
import { permissionGuard, requireActionGuard } from '@shared/auth/guards/permission.guard';
import { unsavedChangesGuard } from '@conecta/feature-testing/unit-testing/shared/unsaved-changes.guard';
import { transferenciaTokenGuard } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-token.guard';
import { transferenciaConfirmarResolver } from '@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.resolver';
import { CONECTA_ROUTES } from '@conecta/shell/conecta.routes';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  // /login no es una ruta: el acceso vive en /auth/login. Sin este alias el comodín muestra el 404.
  {
    path: 'login',
    redirectTo: '/auth/login',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard],
    children: [
      {
        path: 'inicio',
        loadComponent: () => import('@partnercore/feature-home/inicio/inicio.component').then(m => m.InicioComponent),
        title: 'titles.inicio',
      },
      {
        path: 'notifications',
        loadComponent: () => import('@partnercore/feature-modules/modulos/control-general/notificaciones/notifications/notifications.component').then(m => m.NotificationsComponent),
        title: 'titles.notificaciones',
      },
      {
        path: 'configuracion',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
        title: 'titles.configuracion',
      },
      {
        path: 'soporte',
        loadComponent: () => import('@partnercore/feature-settings/soporte/soporte.component').then(m => m.SoporteComponent),
        title: 'titles.soporte',
      },
      {
        path: 'configuracion/transferir',
        loadComponent: () => import('@partnercore/feature-settings/configuracion/transferencia-confirmar/transferencia-confirmar.component').then(m => m.TransferenciaConfirmarComponent),
        title: 'titles.confirmTransfer',
        canActivate: [transferenciaTokenGuard],
        resolve: { transferenciaInfo: transferenciaConfirmarResolver },
      },
      // Dashboard Sistema
      {
        path: 'dashboard-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'dashboard-general',
            pathMatch: 'full',
          },
          {
            path: 'dashboard-general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Admin Sistema
      {
        path: 'admin-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuariosSistema',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisosSistema',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisosSistema',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesSistema',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfilSistema',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresasSistema',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
          {
            path: 'clientes',
            loadComponent: () => import('@partnercore/feature-admin/clientes/clientes.component').then(m => m.ClientesComponent),
            canActivate: [permissionGuard],
            title: 'titles.clientesSistema',
          },
          {
            path: 'planes',
            loadComponent: () => import('@partnercore/feature-admin/planes/planes.component').then(m => m.PlanesComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'planes/:id',
            loadComponent: () => import('@partnercore/feature-admin/planes/components/plan-detail/plan-detail.component').then(m => m.PlanDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.planesSistema',
          },
          {
            path: 'productos',
            loadComponent: () => import('@partnercore/feature-admin/productos/productos.component').then(m => m.ProductosComponent),
            canActivate: [permissionGuard],
            title: 'titles.productosSistema',
          },
        ],
      },
      // Seguridad Sistema (SYS) — ruta top-level para que coincida con la URL sembrada en DB: /seguridad-sistema/*
      {
        path: 'seguridad-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.sesionesSistema',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: true },
            title: 'titles.auditSistema',
          },
        ],
      },
      // Dashboard Control General
      {
        path: 'dashboard',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'general',
            pathMatch: 'full',
          },
          {
            path: 'general',
            loadComponent: () => import('@partnercore/feature-dashboard/dashboard/general/dashboard-general.component').then(m => m.DashboardGeneralComponent),
            canActivate: [permissionGuard],
            title: 'titles.dashboardGeneral',
          },
        ],
      },
      // Módulos Administrativos
      {
        path: 'admin',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'usuarios',
            pathMatch: 'full',
          },
          {
            path: 'usuarios',
            loadComponent: () => import('@partnercore/feature-admin/users/users.component').then(m => m.UsersComponent),
            canActivate: [permissionGuard],
            title: 'titles.usuarios',
          },
          {
            path: 'permisos',
            loadComponent: () => import('@partnercore/feature-admin/permisos').then(m => m.PermisosComponent),
            canActivate: [permissionGuard],
            title: 'titles.permisos',
          },
          {
            path: 'permisos/editar/rol/:id',
            loadComponent: () => import('@partnercore/feature-admin/permisos-editar').then(m => m.PermisosEditarComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.permisos',
          },
          {
            path: 'roles',
            loadComponent: () => import('@partnercore/feature-admin/roles/roles.component').then(m => m.RolesComponent),
            canActivate: [permissionGuard],
            title: 'titles.rolesAdmin',
          },
          {
            path: 'perfil',
            loadComponent: () => import('@partnercore/feature-profile/perfil/perfil.component').then(m => m.PerfilComponent),
            canActivate: [permissionGuard],
            title: 'titles.perfil',
          },
          {
            path: 'empresas',
            loadComponent: () => import('@partnercore/feature-admin/empresas/empresas.component').then(m => m.EmpresasComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresas',
          },
          {
            path: 'empresas/:id',
            loadComponent: () => import('@partnercore/feature-admin/empresas/components/empresa-detail/empresa-detail.component').then(m => m.EmpresaDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.empresaDetalle',
          },
        ],
      },
      // Estructura
      {
        path: 'estructura-system',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'modulos-sistema',
            pathMatch: 'full',
          },
          {
            path: 'modulos-sistema',
            loadComponent: () => import('@partnercore/feature-modules/modulos/module.component').then(m => m.ModuleComponent),
            canActivate: [permissionGuard],
            title: 'titles.modulosSistema',
          },
        ],
      },
      // Maestros Sistema
      {
        path: 'maestros-sistema',
        canActivate: [adminGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedasSistema',
          },
        ],
      },
      // Maestros
      {
        path: 'maestros',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'monedas',
            pathMatch: 'full',
          },
          {
            path: 'monedas',
            loadComponent: () => import('@partnercore/feature-currencies/currencies/currencies.component').then(m => m.CurrenciesComponent),
            canActivate: [permissionGuard],
            title: 'titles.monedas',
          },
        ],
      },
      // Seguridad Control General — ruta top-level para que coincida con la URL sembrada en DB: /seguridad/*
      {
        path: 'seguridad',
        canActivate: [permissionGuard],
        children: [
          {
            path: '',
            redirectTo: 'sesiones',
            pathMatch: 'full',
          },
          {
            path: 'sesiones',
            loadComponent: () => import('@partnercore/feature-sessions/sesiones/sesiones.component').then(m => m.SesionesComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.sesiones',
          },
          {
            path: 'auditorias',
            loadComponent: () => import('@partnercore/feature-audit/auditoria/auditoria.component').then(m => m.AuditoriaComponent),
            canActivate: [permissionGuard],
            data: { isSys: false },
            title: 'titles.audit',
          },
        ],
      },
      // Integraciones: SDK para conectores personalizados + Legacy
      {
        path: 'integraciones',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'conectores', pathMatch: 'full' },
          {
            path: 'conectores',
            loadComponent: () => import('@conecta/feature-connectors/sdk/catalog/connectors-catalog.component').then(m => m.ConnectorsCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.connectors',
          },
          {
            path: 'conectores/nuevo',
            loadComponent: () => import('@conecta/feature-connectors/sdk/wizard/connector-wizard.component').then(m => m.ConnectorWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.connectorNew',
          },
          {
            path: 'conectores/:businessId/editar',
            loadComponent: () => import('@conecta/feature-connectors/sdk/wizard/connector-wizard.component').then(m => m.ConnectorWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.connectorEdit',
          },
          {
            path: 'conectores/:businessId',
            loadComponent: () => import('@conecta/feature-connectors/sdk/detail/connector-detail.component').then(m => m.ConnectorDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.connectorDetail',
          },
          // Legacy Connectors
          {
            path: 'legacy',
            loadComponent: () => import('@conecta/feature-connectors/legacy/catalog/legacy-catalog.component').then(m => m.LegacyCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.legacy',
          },
          {
            path: 'legacy/nuevo',
            loadComponent: () => import('@conecta/feature-connectors/legacy/wizard/legacy-wizard.component').then(m => m.LegacyWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.legacyNew',
          },
          {
            path: 'legacy/permisos',
            loadComponent: () => import('@conecta/feature-connectors/legacy/governance/legacy-permissions.component').then(m => m.LegacyPermissionsComponent),
            canActivate: [permissionGuard],
            title: 'titles.legacyPermissions',
          },
          {
            path: 'legacy/auditoria',
            loadComponent: () => import('@conecta/feature-connectors/legacy/governance/legacy-audit.component').then(m => m.LegacyAuditComponent),
            canActivate: [permissionGuard],
            title: 'titles.legacyAudit',
          },
          {
            path: 'legacy/:businessId/editar',
            loadComponent: () => import('@conecta/feature-connectors/legacy/wizard/legacy-wizard.component').then(m => m.LegacyWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.legacyEdit',
          },
          {
            path: 'legacy/:businessId',
            loadComponent: () => import('@conecta/feature-connectors/legacy/detail/legacy-detail.component').then(m => m.LegacyDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.legacyDetail',
          },
          // MQTT Connectors
          {
            path: 'mqtt',
            loadComponent: () => import('@conecta/feature-connectors/mqtt/catalog/mqtt-catalog.component').then(m => m.MqttCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.mqtt',
          },
          {
            path: 'mqtt/nuevo',
            loadComponent: () => import('@conecta/feature-connectors/mqtt/wizard/mqtt-wizard.component').then(m => m.MqttWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.mqttNew',
          },
          {
            path: 'mqtt/:businessId/editar',
            loadComponent: () => import('@conecta/feature-connectors/mqtt/wizard/mqtt-wizard.component').then(m => m.MqttWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.mqttEdit',
          },
          {
            path: 'mqtt/:businessId',
            loadComponent: () => import('@conecta/feature-connectors/mqtt/detail/mqtt-detail.component').then(m => m.MqttDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.mqttDetail',
          },
          // Orquestación BPM · Motor BPMN (5.1)
          {
            path: 'motor-bpmn',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-bpm/bpm.routes').then(m => m.BPM_ROUTES),
          },
          // Orquestación BPM · Patrón SAGA (5.2)
          {
            path: 'patron-saga',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-saga/saga.routes').then(m => m.SAGA_ROUTES),
          },
          // Orquestación BPM · Scheduler y automatización (5.3)
          {
            path: 'scheduler-automatizacion',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-scheduler/scheduler.routes').then(m => m.SCHEDULER_ROUTES),
          },
          // Gestión Multientorno · Entornos y Despliegue (9.1.1 Entornos aislados)
          {
            path: 'entornos-despliegue',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-environments/environments.routes').then(m => m.ENVIRONMENTS_ROUTES),
          },
          // Gestión Multientorno · Configuración Externalizada (9.1.2)
          {
            path: 'configuracion-externalizada',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-environments/external-config/external-config.routes').then(m => m.EXTERNAL_CONFIG_ROUTES),
          },
          // Gestión Multientorno · Promoción entre entornos (9.1.3)
          {
            path: 'promocion-entornos',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-environments/promotion/promotion.routes').then(m => m.PROMOTION_ROUTES),
          },
          // Gestión Multientorno · Límites y cuotas por entorno (9.1.4)
          {
            path: 'limites-cuotas',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-environments/quotas/quotas.routes').then(m => m.QUOTAS_ROUTES),
          },
          // Infraestructura de Ejecución · Despliegue con Cloud (9.2.1 Despliegue cloud gestionado)
          {
            path: 'despliegue-cloud',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-runtime-infra/managed-cloud/managed-cloud.routes').then(m => m.MANAGED_CLOUD_ROUTES),
          },
          // Infraestructura de Ejecución · Runtime en Kubernetes (9.2.2 RTF)
          {
            path: 'runtime-kubernetes',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-runtime-infra/kubernetes-runtime/kubernetes-runtime.routes').then(m => m.RUNTIME_K8S_ROUTES),
          },
          // Infraestructura de Ejecución · Despliegue on-premise (9.2.3)
          {
            path: 'despliegue-on-premise',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-runtime-infra/onpremise/onpremise.routes').then(m => m.ONPREMISE_ROUTES),
          },
          // Infraestructura de Ejecución · Multi-cloud activo-activo (9.2.4)
          {
            path: 'multi-cloud-activo',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-runtime-infra/multicloud/multicloud.routes').then(m => m.MULTICLOUD_ROUTES),
          },
          // Infraestructura de Ejecución · IaC con Terraform provider (9.2.5)
          {
            path: 'iac-terraform',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-runtime-infra/iac/iac.routes').then(m => m.IAC_ROUTES),
          },
          // Seguridad APIS · Oauth2 Oidc (11.1.1)
          {
            path: 'oauth2-oidc',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-api-security/oauth-oidc/oauth-oidc.routes').then(m => m.OAUTH_OIDC_ROUTES),
          },
          // Seguridad APIS · JWT validación avanzada (11.1.2)
          {
            path: 'jwt-validacion-avanzada',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-api-security/jwt-validation/jwt-validation.routes').then(m => m.JWT_VALIDATION_ROUTES),
          },
          // Seguridad APIS · API Key con rotación (11.1.3)
          {
            path: 'api-key-rotacion',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-api-security/api-keys/api-keys.routes').then(m => m.API_KEYS_ROUTES),
          },
          // Seguridad APIS · Mutual TLS mtls (11.1.4)
          {
            path: 'mutual-tls-mtls',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-api-security/mutual-tls/mutual-tls.routes').then(m => m.MUTUAL_TLS_ROUTES),
          },
          // Seguridad APIS · Políticas personalizadas (11.1.5)
          {
            path: 'politicas-personalizadas',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-api-security/custom-policies/custom-policies.routes').then(m => m.CUSTOM_POLICIES_ROUTES),
          },
          // Devops CI CD · Quality Gates y, debajo, Despliegue Blue Green
          {
            path: 'quality-gates',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-devops/quality-gates/quality-gates.routes').then(m => m.QUALITY_GATES_ROUTES),
          },
          {
            path: 'despliegue-blue-green',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-devops/blue-green/blue-green.routes').then(m => m.BLUE_GREEN_ROUTES),
          },
          // Devops CI CD · Rollback Automático (debajo de Despliegue Blue Green)
          {
            path: 'rollback-automatico',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-devops/rollback/rollback.routes').then(m => m.ROLLBACK_ROUTES),
          },
          // Devops CI CD · Plugins Jenkins GitHub
          {
            path: 'plugins-jenkins-github',
            canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-devops/jenkins-github/jenkins-github.routes').then(m => m.JENKINS_GITHUB_ROUTES),
          },
          {
            path: 'dashboards-metricas', canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-dashboards-metricas/dashboards-metricas.routes').then(m => m.DASHBOARD_ROUTES),
          },
          {
            path: 'trazabilidad-logs', canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-trazabilidad-logs/tracing-logs.routes').then(m => m.TRACING_LOGS_ROUTES),
          },
          {
            path: 'inteligencia-alertas', canActivate: [permissionGuard],
            loadChildren: () => import('@conecta/feature-inteligencia-alertas/inteligencia-alertas.routes').then(m => m.INTELIGENCIA_ALERTAS_ROUTES),
          },
          // Gestión de Versiones
          {
            path: 'versiones',
            loadComponent: () => import('@conecta/feature-connectors/versioning/catalog/connector-versions-catalog.component').then(m => m.ConnectorVersionsCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.versiones',
          },
          {
            path: 'versiones/nuevo',
            loadComponent: () => import('@conecta/feature-connectors/versioning/catalog/connector-catalog-form.component').then(m => m.ConnectorCatalogFormComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.versionesNew',
          },
          {
            path: 'versiones/dashboard',
            loadComponent: () => import('@conecta/feature-connectors/versioning/dashboard/connector-versioning-dashboard.component').then(m => m.ConnectorVersioningDashboardComponent),
            canActivate: [permissionGuard],
            title: 'titles.versionesDashboard',
          },
          {
            path: 'versiones/:catalogBusinessId',
            loadComponent: () => import('@conecta/feature-connectors/versioning/versions/connector-version-list.component').then(m => m.ConnectorVersionListComponent),
            canActivate: [permissionGuard],
            title: 'titles.versionesDetail',
          },
          {
            path: 'versiones/:catalogBusinessId/editar',
            loadComponent: () => import('@conecta/feature-connectors/versioning/catalog/connector-catalog-form.component').then(m => m.ConnectorCatalogFormComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.versionesEdit',
          },
          {
            path: 'versiones/:catalogBusinessId/nueva',
            loadComponent: () => import('@conecta/feature-connectors/versioning/wizard/connector-version-wizard.component').then(m => m.ConnectorVersionWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.versionesNew',
          },
          {
            path: 'versiones/:catalogBusinessId/versiones/:versionBusinessId/editar',
            loadComponent: () => import('@conecta/feature-connectors/versioning/wizard/connector-version-wizard.component').then(m => m.ConnectorVersionWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.versionesEdit',
          },
          // Catálogo de APIs
          {
            path: 'apis',
            loadComponent: () => import('@conecta/feature-repositories/apis-catalog/catalog/apis-catalog.component').then(m => m.ApisCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.apis',
          },
          {
            path: 'apis/nuevo',
            loadComponent: () => import('@conecta/feature-repositories/apis-catalog/wizard/api-wizard.component').then(m => m.ApiWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.apisNew',
          },
          {
            path: 'apis/:id/editar',
            loadComponent: () => import('@conecta/feature-repositories/apis-catalog/wizard/api-wizard.component').then(m => m.ApiWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.apisEdit',
          },
          {
            path: 'apis/:id',
            loadComponent: () => import('@conecta/feature-repositories/apis-catalog/detail/api-detail.component').then(m => m.ApiDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.apisDetail',
          },
          // Plantillas de integración
          {
            path: 'plantillas',
            loadComponent: () => import('@conecta/feature-repositories/integration-templates/catalog/templates-catalog.component').then(m => m.TemplatesCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.templates',
          },
          {
            path: 'plantillas/nueva',
            loadComponent: () => import('@conecta/feature-repositories/integration-templates/wizard/template-wizard.component').then(m => m.TemplateWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.templatesNew',
          },
          {
            path: 'plantillas/marketplace',
            loadComponent: () => import('@conecta/feature-repositories/integration-templates/marketplace/templates-marketplace.component').then(m => m.TemplatesMarketplaceComponent),
            canActivate: [permissionGuard],
            title: 'titles.templatesMarketplace',
          },
          {
            path: 'plantillas/:id/editar',
            loadComponent: () => import('@conecta/feature-repositories/integration-templates/wizard/template-wizard.component').then(m => m.TemplateWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.templatesEdit',
          },
          {
            path: 'plantillas/:id/instanciar',
            loadComponent: () => import('@conecta/feature-repositories/integration-templates/instance/template-instance.component').then(m => m.TemplateInstanceComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.templatesInstance',
          },
          {
            path: 'plantillas/:id',
            loadComponent: () => import('@conecta/feature-repositories/integration-templates/detail/template-detail.component').then(m => m.TemplateDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.templatesDetail',
          },
          // Métricas de adopción
          {
            path: 'metricas-adopcion',
            loadComponent: () => import('@conecta/feature-repositories/adoption-metrics/home/adoption-metrics-home.component').then(m => m.AdoptionMetricsHomeComponent),
            canActivate: [permissionGuard],
            title: 'titles.metricasAdopcion',
          },
          {
            path: 'metricas-adopcion/dashboard',
            loadComponent: () => import('@conecta/feature-repositories/adoption-metrics/dashboard/adoption-metrics-dashboard.component').then(m => m.AdoptionMetricsDashboardComponent),
            canActivate: [permissionGuard],
            title: 'titles.metricasAdopcionDashboard',
          },
          {
            path: 'metricas-adopcion/huerfanos',
            loadComponent: () => import('@conecta/feature-repositories/adoption-metrics/orphan-assets/orphan-assets.component').then(m => m.OrphanAssetsComponent),
            canActivate: [permissionGuard],
            title: 'titles.metricasAdopcionHuerfanos',
          },
          {
            path: 'metricas-adopcion/configuracion',
            loadComponent: () => import('@conecta/feature-repositories/adoption-metrics/settings/adoption-settings.component').then(m => m.AdoptionSettingsComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.metricasAdopcionConfiguracion',
          },
          {
            path: 'metricas-adopcion/gobierno',
            loadComponent: () => import('@conecta/feature-repositories/adoption-metrics/governance/adoption-governance.component').then(m => m.AdoptionGovernanceComponent),
            canActivate: [permissionGuard],
            title: 'titles.metricasAdopcionGobierno',
          },
          // Fragmentos reutilizables
          {
            path: 'fragmentos',
            loadComponent: () => import('@conecta/feature-repositories/reusable-fragment/catalog/reusable-fragments-catalog.component').then(m => m.ReusableFragmentsCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.fragments',
          },
          {
            path: 'fragmentos/nuevo',
            loadComponent: () => import('@conecta/feature-repositories/reusable-fragment/wizard/reusable-fragment-wizard.component').then(m => m.ReusableFragmentWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.fragmentsNew',
          },
          {
            path: 'fragmentos/dashboard',
            loadComponent: () => import('@conecta/feature-repositories/reusable-fragment/dashboard/reusable-fragments-dashboard.component').then(m => m.ReusableFragmentsDashboardComponent),
            canActivate: [permissionGuard],
            title: 'titles.fragmentsDashboard',
          },
          {
            path: 'fragmentos/:id/editar',
            loadComponent: () => import('@conecta/feature-repositories/reusable-fragment/wizard/reusable-fragment-wizard.component').then(m => m.ReusableFragmentWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.fragmentsEdit',
          },
          {
            path: 'fragmentos/:id',
            loadComponent: () => import('@conecta/feature-repositories/reusable-fragment/detail/reusable-fragment-detail.component').then(m => m.ReusableFragmentDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.fragmentsDetail',
          },
          // Portal público/privado
          {
            path: 'portal-publico',
            loadComponent: () => import('@conecta/feature-repositories/portal-publico/catalog/portal-catalog.component').then(m => m.PortalCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.portal',
          },
          {
            path: 'portal-publico/nuevo',
            loadComponent: () => import('@conecta/feature-repositories/portal-publico/wizard/portal-wizard.component').then(m => m.PortalWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.portalNew',
          },
          {
            path: 'portal-publico/dashboard',
            loadComponent: () => import('@conecta/feature-repositories/portal-publico/dashboard/portal-dashboard.component').then(m => m.PortalDashboardComponent),
            canActivate: [permissionGuard],
            title: 'titles.portalDashboard',
          },
          {
            path: 'portal-publico/:id/editar',
            loadComponent: () => import('@conecta/feature-repositories/portal-publico/wizard/portal-wizard.component').then(m => m.PortalWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.portalEdit',
          },
          {
            path: 'portal-publico/:id',
            loadComponent: () => import('@conecta/feature-repositories/portal-publico/detail/portal-detail.component').then(m => m.PortalDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.portalDetail',
          },
          // Búsqueda semántica con IA
          {
            path: 'busqueda-semantica',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/search/semantic-search.component').then(m => m.SemanticSearchPageComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticSearch',
          },
          {
            path: 'busqueda-semantica/resultados',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/search-results/search-results.component').then(m => m.SemanticSearchResultsComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticSearchResults',
          },
          {
            path: 'busqueda-semantica/activos/:id',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/asset-detail/asset-detail.component').then(m => m.SemanticAssetDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticAssetDetail',
          },
          {
            path: 'busqueda-semantica/indice',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/index-catalog/index-catalog.component').then(m => m.SemanticIndexCatalogComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticIndex',
          },
          {
            path: 'busqueda-semantica/indice/nuevo',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/index-config/index-config.component').then(m => m.SemanticIndexConfigComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.semanticIndexNew',
          },
          {
            path: 'busqueda-semantica/indice/dashboard',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/index-dashboard/index-dashboard.component').then(m => m.SemanticIndexDashboardComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticDashboard',
          },
          {
            path: 'busqueda-semantica/indice/:id/configuracion',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/index-config/index-config.component').then(m => m.SemanticIndexConfigComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.semanticIndexConfig',
          },
          {
            path: 'busqueda-semantica/indice/:id/fuentes',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/index-sources/index-sources.component').then(m => m.SemanticIndexSourcesComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticIndexSources',
          },
          {
            path: 'busqueda-semantica/indice/:id/validacion',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/index-validation/index-validation.component').then(m => m.SemanticIndexValidationComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticIndexValidation',
          },
          {
            path: 'busqueda-semantica/indice/:id/preview',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/index-preview/index-preview.component').then(m => m.SemanticIndexPreviewComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticIndexPreview',
          },
          {
            path: 'busqueda-semantica/indice/:id/historial',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/index-history/index-history.component').then(m => m.SemanticIndexHistoryComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticHistory',
          },
          {
            path: 'busqueda-semantica/duplicados',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/duplicate-candidates/duplicate-candidates.component').then(m => m.SemanticDuplicateCandidatesComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticDuplicates',
          },
          {
            path: 'busqueda-semantica/duplicados/:id/comparar',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/duplicate-comparison/duplicate-comparison.component').then(m => m.SemanticDuplicateComparisonComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticDuplicateCompare',
          },
          {
            path: 'busqueda-semantica/duplicados/:id/decision',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/duplicate-decision/duplicate-decision.component').then(m => m.SemanticDuplicateDecisionComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.semanticDuplicateDecision',
          },
          {
            path: 'busqueda-semantica/auditoria',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/audit/semantic-audit.component').then(m => m.SemanticAuditComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticAudit',
          },
          {
            path: 'busqueda-semantica/mapa',
            loadComponent: () => import('@conecta/feature-repositories/semantic-search/pages/screen-map/screen-map.component').then(m => m.SemanticScreenMapComponent),
            canActivate: [permissionGuard],
            title: 'titles.semanticScreenMap',
          },
          // Diseñador visual de integraciones — Diseño drag-and-drop (2.1.1)
          {
            path: 'diseno-drag-drop',
            loadComponent: () => import('@conecta/feature-designer/drag-drop/pages/flow-list/flow-list.component').then(m => m.FlowListComponent),
            canActivate: [permissionGuard],
            title: 'titles.flowDesigner',
          },
          {
            path: 'diseno-drag-drop/nuevo',
            loadComponent: () => import('@conecta/feature-designer/drag-drop/pages/flow-editor/flow-editor.component').then(m => m.FlowEditorComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.flowDesignerNew',
          },
          {
            // Sin permiso de edición el canvas se abre en solo lectura (RNF-004).
            path: 'diseno-drag-drop/:id/editar',
            loadComponent: () => import('@conecta/feature-designer/drag-drop/pages/flow-editor/flow-editor.component').then(m => m.FlowEditorComponent),
            canActivate: [permissionGuard],
            title: 'titles.flowDesignerEditor',
          },
          {
            path: 'diseno-drag-drop/:id',
            loadComponent: () => import('@conecta/feature-designer/drag-drop/pages/flow-overview/flow-overview.component').then(m => m.FlowOverviewComponent),
            canActivate: [permissionGuard],
            title: 'titles.flowDesignerDetail',
          },
          // Diseñador visual de integraciones — Modo código dual (2.1.2)
          {
            path: 'modo-codigo-dual',
            loadComponent: () => import('@conecta/feature-designer/dual-code/pages/dual-flow-list/dual-flow-list.component').then(m => m.DualFlowListComponent),
            canActivate: [permissionGuard],
            title: 'titles.dualCode',
          },
          {
            // La vista (`?vista=dividida|codigo|visual`) se valida contra RBAC dentro del editor (RN-006).
            path: 'modo-codigo-dual/:id',
            loadComponent: () => import('@conecta/feature-designer/dual-code/pages/dual-code-editor/dual-code-editor.component').then(m => m.DualCodeEditorComponent),
            canActivate: [permissionGuard],
            title: 'titles.dualCodeEditor',
          },
          // Diseñador visual de integraciones — Templates y wizards (2.1.3)
          {
            path: 'templates-wizards',
            loadComponent: () => import('@conecta/feature-designer/templates-wizards/pages/template-gallery/template-gallery.component').then(m => m.TemplateGalleryComponent),
            canActivate: [permissionGuard],
            title: 'titles.templateWizard',
          },
          {
            // TEMPLATE_PUBLISH → canEdit del módulo. `?flujo=<id>` preselecciona el flujo de origen.
            path: 'templates-wizards/publicar',
            loadComponent: () => import('@conecta/feature-designer/templates-wizards/pages/template-publish/template-publish.component').then(m => m.TemplatePublishComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.templateWizardPublish',
          },
          {
            path: 'templates-wizards/:id',
            loadComponent: () => import('@conecta/feature-designer/templates-wizards/pages/template-detail/template-detail.component').then(m => m.TemplateDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.templateWizardDetail',
          },
          {
            // TEMPLATE_WIZARD_START / TEMPLATE_INSTANTIATE → canCreate del módulo.
            path: 'templates-wizards/:id/instanciar',
            loadComponent: () => import('@conecta/feature-designer/templates-wizards/pages/template-wizard/template-wizard.component').then(m => m.TemplateWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.templateWizardInstantiate',
          },
          // Diseñador visual de integraciones — AI Flow Suggest (2.1.4). La asistencia contextual
          // vive en el canvas de `diseno-drag-drop`; aquí: configuración, métricas y flujos similares.
          {
            path: 'ai-flow-suggest',
            loadComponent: () => import('@conecta/feature-designer/ai-flow-suggest/pages/ai-flow-suggest-home/ai-flow-suggest-home.component').then(m => m.AiFlowSuggestHomeComponent),
            canActivate: [permissionGuard],
            title: 'titles.aiFlowSuggest',
          },
          // Diseñador visual de integraciones — Colaboración en tiempo real (2.1.5). La colaboración
          // vive en el canvas de `diseno-drag-drop`; aquí: alcance, roles y flujos colaborables.
          {
            path: 'colaboracion-tiempo-real',
            loadComponent: () => import('@conecta/feature-designer/collaboration/pages/collaboration-home/collaboration-home.component').then(m => m.CollaborationHomeComponent),
            canActivate: [permissionGuard],
            title: 'titles.collaboration',
          },
          ...CONECTA_ROUTES,
          // Testing de integraciones — Unit testing de flujos (2.3.1). Crear (canCreate), editar (canEdit),
          // eliminar (canDelete), ejecutar (canCreate o canEdit) y suites/CI (canEdit) se controlan en las vistas.
          {
            path: 'unit-testing-flujos',
            loadComponent: () => import('@conecta/feature-testing/unit-testing/pages/unit-testing-overview/unit-testing-overview.component').then(m => m.UnitTestingOverviewComponent),
            canActivate: [permissionGuard],
            title: 'titles.unitTesting',
          },
          {
            // TEST_CASE_CREATE → canCreate del módulo.
            path: 'unit-testing-flujos/nuevo',
            loadComponent: () => import('@conecta/feature-testing/unit-testing/pages/test-case-editor/test-case-editor.component').then(m => m.TestCaseEditorComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            canDeactivate: [unsavedChangesGuard],
            title: 'titles.unitTestingNew',
          },
          {
            // `?case=<id>&autorun=1` abre y ejecuta un caso (TEST_CASE_EXECUTE).
            path: 'unit-testing-flujos/ejecucion',
            loadComponent: () => import('@conecta/feature-testing/unit-testing/pages/test-run/test-run.component').then(m => m.TestRunComponent),
            canActivate: [permissionGuard],
            title: 'titles.unitTestingRun',
          },
          {
            path: 'unit-testing-flujos/ejecuciones/:executionId',
            loadComponent: () => import('@conecta/feature-testing/unit-testing/pages/test-execution-report/test-execution-report.component').then(m => m.TestExecutionReportComponent),
            canActivate: [permissionGuard],
            title: 'titles.unitTestingExecution',
          },
          {
            // `?gate=<id>` selecciona una corrida. TEST_SUITE_RUN / configurar CI → canEdit; excepciones → canDelete.
            path: 'unit-testing-flujos/pipelines',
            loadComponent: () => import('@conecta/feature-testing/unit-testing/pages/test-pipelines/test-pipelines.component').then(m => m.TestPipelinesComponent),
            canActivate: [permissionGuard],
            title: 'titles.unitTestingPipelines',
          },
          {
            path: 'unit-testing-flujos/historial',
            loadComponent: () => import('@conecta/feature-testing/unit-testing/pages/test-history/test-history.component').then(m => m.TestHistoryComponent),
            canActivate: [permissionGuard],
            title: 'titles.unitTestingHistory',
          },
          {
            // Sin canEdit la vista se abre en solo lectura.
            path: 'unit-testing-flujos/:id/editar',
            loadComponent: () => import('@conecta/feature-testing/unit-testing/pages/test-case-editor/test-case-editor.component').then(m => m.TestCaseEditorComponent),
            canActivate: [permissionGuard],
            canDeactivate: [unsavedChangesGuard],
            title: 'titles.unitTestingEdit',
          },
          // Testing de integraciones — Pruebas de contrato Pact (2.3.2). Ver y can-i-deploy (canView), publicar
          // (canCreate), verificar y solicitar excepciones (canEdit), archivar y aprobar excepciones (canDelete).
          {
            path: 'pruebas-contrato',
            loadComponent: () => import('@conecta/feature-testing/contract-testing/pages/pact-dashboard/pact-dashboard.component').then(m => m.PactDashboardComponent),
            canActivate: [permissionGuard],
            title: 'titles.contractTesting',
          },
          {
            // PACT_PUBLISH → canCreate del módulo.
            path: 'pruebas-contrato/publicar',
            loadComponent: () => import('@conecta/feature-testing/contract-testing/pages/pact-publish/pact-publish.component').then(m => m.PactPublishComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            canDeactivate: [unsavedChangesGuard],
            title: 'titles.contractTestingPublish',
          },
          {
            // `?provider=<nombre>&env=<entorno>` preselecciona el productor.
            path: 'pruebas-contrato/verificaciones',
            loadComponent: () => import('@conecta/feature-testing/contract-testing/pages/pact-verification/pact-verification.component').then(m => m.PactVerificationComponent),
            canActivate: [permissionGuard],
            title: 'titles.contractTestingVerification',
          },
          {
            path: 'pruebas-contrato/compatibilidad',
            loadComponent: () => import('@conecta/feature-testing/contract-testing/pages/pact-compatibility/pact-compatibility.component').then(m => m.PactCompatibilityComponent),
            canActivate: [permissionGuard],
            title: 'titles.contractTestingMatrix',
          },
          {
            // `?service=<nombre>&version=<v>&env=<entorno>` precarga la consulta can-i-deploy.
            path: 'pruebas-contrato/despliegues',
            loadComponent: () => import('@conecta/feature-testing/contract-testing/pages/pact-deployments/pact-deployments.component').then(m => m.PactDeploymentsComponent),
            canActivate: [permissionGuard],
            title: 'titles.contractTestingDeployments',
          },
          {
            // `?tab=interactions|verifications|traceability|formal` abre una pestaña.
            path: 'pruebas-contrato/:id',
            loadComponent: () => import('@conecta/feature-testing/contract-testing/pages/pact-detail/pact-detail.component').then(m => m.PactDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.contractTestingDetail',
          },
          // Testing de integraciones — Pruebas de carga (2.3.3). Ver (canView), crear perfil (canCreate), editar perfil,
          // umbrales y baseline (canEdit), ejecutar/detener (canCreate o canEdit) y eliminar (canDelete) en las vistas.
          {
            // `?tab=history` abre el historial; `q|type|status|execution|period|page` conservan los filtros.
            path: 'pruebas-carga',
            loadComponent: () => import('@conecta/feature-testing/load-testing/pages/load-test-list/load-test-list.component').then(m => m.LoadTestListComponent),
            canActivate: [permissionGuard],
            title: 'titles.loadTesting',
          },
          {
            // LOAD_PROFILE_CREATE → canCreate del módulo.
            path: 'pruebas-carga/nueva',
            loadComponent: () => import('@conecta/feature-testing/load-testing/pages/load-test-wizard/load-test-wizard.component').then(m => m.LoadTestWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            canDeactivate: [unsavedChangesGuard],
            title: 'titles.loadTestingNew',
          },
          {
            path: 'pruebas-carga/ejecuciones/:executionId',
            loadComponent: () => import('@conecta/feature-testing/load-testing/pages/load-test-monitor/load-test-monitor.component').then(m => m.LoadTestMonitorComponent),
            canActivate: [permissionGuard],
            title: 'titles.loadTestingExecution',
          },
          {
            path: 'pruebas-carga/ejecuciones/:executionId/resultados',
            loadComponent: () => import('@conecta/feature-testing/load-testing/pages/load-test-results/load-test-results.component').then(m => m.LoadTestResultsComponent),
            canActivate: [permissionGuard],
            title: 'titles.loadTestingResults',
          },
          {
            // `?tab=metrics|infra|events` abre una pestaña.
            path: 'pruebas-carga/ejecuciones/:executionId/reporte',
            loadComponent: () => import('@conecta/feature-testing/load-testing/pages/load-test-report/load-test-report.component').then(m => m.LoadTestReportComponent),
            canActivate: [permissionGuard],
            title: 'titles.loadTestingReport',
          },
          {
            path: 'pruebas-carga/:profileId/editar',
            loadComponent: () => import('@conecta/feature-testing/load-testing/pages/load-test-wizard/load-test-wizard.component').then(m => m.LoadTestWizardComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            canDeactivate: [unsavedChangesGuard],
            title: 'titles.loadTestingEdit',
          },
          {
            path: 'pruebas-carga/:profileId',
            loadComponent: () => import('@conecta/feature-testing/load-testing/pages/load-profile-detail/load-profile-detail.component').then(m => m.LoadProfileDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.loadTestingProfile',
          },
          // Testing de integraciones — Cobertura de código (2.3.4). Ver corridas, historial, quality gate y reportes
          // (canView); calcular cobertura y crear umbrales (canCreate); editar/activar umbrales y excluir componentes
          // (canEdit); eliminar umbrales (canDelete). Las acciones se controlan en las vistas.
          {
            // `q|version|result|trigger|from|to|page` conservan los filtros.
            path: 'cobertura-codigo',
            loadComponent: () => import('@conecta/feature-testing/code-coverage/pages/coverage-runs/coverage-runs.component').then(m => m.CoverageRunsComponent),
            canActivate: [permissionGuard],
            title: 'titles.codeCoverage',
          },
          {
            // `?tab=canvas|components|branches|summary` abre una pestaña; `?node=<id>` selecciona un componente.
            path: 'cobertura-codigo/ejecuciones/:runId',
            loadComponent: () => import('@conecta/feature-testing/code-coverage/pages/coverage-run-detail/coverage-run-detail.component').then(m => m.CoverageRunDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.codeCoverageRun',
          },
          {
            // `?run=<id>` selecciona una evaluación.
            path: 'cobertura-codigo/quality-gate',
            loadComponent: () => import('@conecta/feature-testing/code-coverage/pages/coverage-quality-gates/coverage-quality-gates.component').then(m => m.CoverageQualityGatesComponent),
            canActivate: [permissionGuard],
            title: 'titles.codeCoverageQualityGate',
          },
          {
            // `flow|version|period|trigger|result` conservan los filtros.
            path: 'cobertura-codigo/historial',
            loadComponent: () => import('@conecta/feature-testing/code-coverage/pages/coverage-history/coverage-history.component').then(m => m.CoverageHistoryComponent),
            canActivate: [permissionGuard],
            title: 'titles.codeCoverageHistory',
          },
          {
            path: 'cobertura-codigo/umbrales',
            loadComponent: () => import('@conecta/feature-testing/code-coverage/pages/coverage-thresholds/coverage-thresholds.component').then(m => m.CoverageThresholdsComponent),
            canActivate: [permissionGuard],
            title: 'titles.codeCoverageThresholds',
          },
          {
            // `tab=historial|baselines`; en Resumen `q|pr|result|severity|gate|from|to|period|page` conservan los filtros.
            path: 'regresion-testing',
            loadComponent: () => import('@conecta/feature-testing/regression-testing/pages/regression-dashboard/regression-dashboard.component').then(m => m.RegressionDashboardComponent),
            canActivate: [permissionGuard],
            title: 'titles.regressionTesting',
          },
          {
            // `?tab=resumen|resultados|pruebas|logs|comparacion` abre una pestaña.
            path: 'regresion-testing/ejecuciones/:runId',
            loadComponent: () => import('@conecta/feature-testing/regression-testing/pages/regression-run-detail/regression-run-detail.component').then(m => m.RegressionRunDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.regressionTestingRun',
          },
          {
            path: 'regresion-testing/ejecuciones/:runId/seleccion',
            loadComponent: () => import('@conecta/feature-testing/regression-testing/pages/regression-impact/regression-impact.component').then(m => m.RegressionImpactComponent),
            canActivate: [permissionGuard],
            title: 'titles.regressionTestingImpact',
          },
          {
            // `?case=<caseKey>` selecciona un caso en el visor.
            path: 'regresion-testing/ejecuciones/:runId/comparacion',
            loadComponent: () => import('@conecta/feature-testing/regression-testing/pages/regression-comparison/regression-comparison.component').then(m => m.RegressionComparisonComponent),
            canActivate: [permissionGuard],
            title: 'titles.regressionTestingComparison',
          },
          {
            path: 'regresion-testing/pull-requests/:prId',
            loadComponent: () => import('@conecta/feature-testing/regression-testing/pages/regression-pull-request/regression-pull-request.component').then(m => m.RegressionPullRequestComponent),
            canActivate: [permissionGuard],
            title: 'titles.regressionTestingPullRequest',
          },
          // IDE avanzado (Pro-Code) — IDE cloud basado en VS Code (2.2.1)
          {
            path: 'ide-cloud',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/workspace-list/workspace-list.component').then(m => m.WorkspaceListComponent),
            canActivate: [permissionGuard],
            title: 'titles.ideCloud',
          },
          {
            // IDE_CREATE_WORKSPACE → canCreate del módulo.
            path: 'ide-cloud/nuevo',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/workspace-create/workspace-create.component').then(m => m.WorkspaceCreateComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.ideCloudNew',
          },
          {
            path: 'ide-cloud/extensiones',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/extensions-marketplace/extensions-marketplace.component').then(m => m.ExtensionsMarketplaceComponent),
            canActivate: [permissionGuard],
            title: 'titles.ideCloudExtensions',
          },
          {
            // Solicitar (canCreate) y aprobar (canDelete) se controlan dentro de la vista.
            path: 'ide-cloud/break-glass',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/break-glass/break-glass.component').then(m => m.BreakGlassComponent),
            canActivate: [permissionGuard],
            title: 'titles.ideCloudBreakGlass',
          },
          {
            path: 'ide-cloud/:id/aprovisionamiento',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/workspace-provisioning/workspace-provisioning.component').then(m => m.WorkspaceProvisioningComponent),
            canActivate: [permissionGuard],
            title: 'titles.ideCloudProvisioning',
          },
          {
            path: 'ide-cloud/:id/terminal',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/workspace-terminal/workspace-terminal.component').then(m => m.WorkspaceTerminalComponent),
            canActivate: [permissionGuard],
            title: 'titles.ideCloudTerminal',
          },
          {
            path: 'ide-cloud/:id/extensiones',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/extensions-marketplace/extensions-marketplace.component').then(m => m.ExtensionsMarketplaceComponent),
            canActivate: [permissionGuard],
            title: 'titles.ideCloudExtensions',
          },
          {
            path: 'ide-cloud/:id/detalle',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/workspace-detail/workspace-detail.component').then(m => m.WorkspaceDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.ideCloudDetail',
          },
          {
            // IDE_SHARE_WORKSPACE → canEdit del módulo.
            path: 'ide-cloud/:id/compartir',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/workspace-share/workspace-share.component').then(m => m.WorkspaceShareComponent),
            canActivate: [permissionGuard, requireActionGuard('edit')],
            title: 'titles.ideCloudShare',
          },
          {
            // `?breakglass=<id>` abre un workspace productivo en solo lectura (RF-024).
            path: 'ide-cloud/:id',
            loadComponent: () => import('@conecta/feature-ide/ide-cloud/pages/ide-editor/ide-editor.component').then(m => m.IdeEditorComponent),
            canActivate: [permissionGuard],
            title: 'titles.ideCloudEditor',
          },
          // IDE avanzado (Pro-Code) — Autocompletado inteligente (2.2.2). `?workspace=<id>` del IDE cloud.
          {
            path: 'autocompletado-inteligente',
            loadComponent: () => import('@conecta/feature-ide/intelligent-autocomplete/pages/connector-autocomplete/connector-autocomplete.component').then(m => m.ConnectorAutocompleteComponent),
            canActivate: [permissionGuard],
            title: 'titles.autocomplete',
          },
          {
            path: 'autocompletado-inteligente/validacion',
            loadComponent: () => import('@conecta/feature-ide/intelligent-autocomplete/pages/type-validation/type-validation.component').then(m => m.TypeValidationComponent),
            canActivate: [permissionGuard],
            title: 'titles.autocompleteValidation',
          },
          {
            path: 'autocompletado-inteligente/asistente-ia',
            loadComponent: () => import('@conecta/feature-ide/intelligent-autocomplete/pages/ai-assistant/ai-assistant.component').then(m => m.AiAssistantComponent),
            canActivate: [permissionGuard],
            title: 'titles.autocompleteAssistant',
          },
          {
            path: 'autocompletado-inteligente/sugerencias',
            loadComponent: () => import('@conecta/feature-ide/intelligent-autocomplete/pages/contextual-suggestions/contextual-suggestions.component').then(m => m.ContextualSuggestionsComponent),
            canActivate: [permissionGuard],
            title: 'titles.autocompleteSuggestions',
          },
          {
            // Crear (canCreate), editar (canEdit) y revisar/eliminar (canDelete) se controlan dentro de la vista.
            path: 'autocompletado-inteligente/snippets',
            loadComponent: () => import('@conecta/feature-ide/intelligent-autocomplete/pages/team-snippets/team-snippets.component').then(m => m.TeamSnippetsComponent),
            canActivate: [permissionGuard],
            title: 'titles.autocompleteSnippets',
          },
          {
            // AI_POLICY_WRITE → canDelete del módulo (Platform Admin).
            path: 'autocompletado-inteligente/gobierno',
            loadComponent: () => import('@conecta/feature-ide/intelligent-autocomplete/pages/ai-governance/ai-governance.component').then(m => m.AiGovernanceComponent),
            canActivate: [permissionGuard, requireActionGuard('delete')],
            title: 'titles.autocompleteGovernance',
          },
          // IDE avanzado (Pro-Code) — Depurador de flujos (2.2.3). `?workspace=&flow=` del IDE cloud;
          // iniciar sesión / break-glass (canCreate) y breakpoints (canEdit) se controlan dentro de la vista.
          {
            path: 'depurador-flujos',
            loadComponent: () => import('@conecta/feature-ide/flow-debugger/pages/debug-overview/debug-overview.component').then(m => m.DebugOverviewComponent),
            canActivate: [permissionGuard],
            title: 'titles.flowDebugger',
          },
          {
            path: 'depurador-flujos/breakpoints',
            loadComponent: () => import('@conecta/feature-ide/flow-debugger/pages/debug-breakpoints/debug-breakpoints.component').then(m => m.DebugBreakpointsComponent),
            canActivate: [permissionGuard],
            title: 'titles.flowDebuggerBreakpoints',
          },
          {
            path: 'depurador-flujos/inspeccion',
            loadComponent: () => import('@conecta/feature-ide/flow-debugger/pages/debug-inspection/debug-inspection.component').then(m => m.DebugInspectionComponent),
            canActivate: [permissionGuard],
            title: 'titles.flowDebuggerInspection',
          },
          {
            path: 'depurador-flujos/paso-a-paso',
            loadComponent: () => import('@conecta/feature-ide/flow-debugger/pages/debug-stepping/debug-stepping.component').then(m => m.DebugSteppingComponent),
            canActivate: [permissionGuard],
            title: 'titles.flowDebuggerStepping',
          },
          {
            // Solicitar (canCreate) y aprobar (canDelete, Security Officer) se controlan dentro de la vista.
            path: 'depurador-flujos/break-glass',
            loadComponent: () => import('@conecta/feature-ide/flow-debugger/pages/debug-breakglass/debug-breakglass.component').then(m => m.DebugBreakglassComponent),
            canActivate: [permissionGuard],
            title: 'titles.flowDebuggerBreakglass',
          },
          {
            // Auditoría de depuración → canDelete del módulo (Security Officer).
            path: 'depurador-flujos/auditoria',
            loadComponent: () => import('@conecta/feature-ide/flow-debugger/pages/debug-audit/debug-audit.component').then(m => m.DebugAuditComponent),
            canActivate: [permissionGuard, requireActionGuard('delete')],
            title: 'titles.flowDebuggerAudit',
          },
          // IDE avanzado (Pro-Code) — Integración Git nativa (2.2.4). `?workspace=` del IDE cloud; staging, commit,
          // push/pull y PR (canCreate), revisión (canEdit) y fusión (canDelete) se controlan dentro de cada vista.
          {
            path: 'integracion-git',
            loadComponent: () => import('@conecta/feature-ide/git-integration/pages/git-repository/git-repository.component').then(m => m.GitRepositoryComponent),
            canActivate: [permissionGuard],
            title: 'titles.gitIntegration',
          },
          {
            path: 'integracion-git/ramas',
            loadComponent: () => import('@conecta/feature-ide/git-integration/pages/git-branches/git-branches.component').then(m => m.GitBranchesComponent),
            canActivate: [permissionGuard],
            title: 'titles.gitIntegrationBranches',
          },
          {
            path: 'integracion-git/pull-requests',
            loadComponent: () => import('@conecta/feature-ide/git-integration/pages/git-pull-requests/git-pull-requests.component').then(m => m.GitPullRequestsComponent),
            canActivate: [permissionGuard],
            title: 'titles.gitIntegrationPullRequests',
          },
          {
            // Crear PR → GIT_PR_CREATE (canCreate). Debe ir antes de `pull-requests/:id`.
            path: 'integracion-git/pull-requests/nuevo',
            loadComponent: () => import('@conecta/feature-ide/git-integration/pages/git-pull-request-create/git-pull-request-create.component').then(m => m.GitPullRequestCreateComponent),
            canActivate: [permissionGuard, requireActionGuard('create')],
            title: 'titles.gitIntegrationPullRequestNew',
          },
          {
            path: 'integracion-git/pull-requests/:id',
            loadComponent: () => import('@conecta/feature-ide/git-integration/pages/git-pull-request-detail/git-pull-request-detail.component').then(m => m.GitPullRequestDetailComponent),
            canActivate: [permissionGuard],
            title: 'titles.gitIntegrationPullRequestDetail',
          },
          {
            path: 'integracion-git/conflictos',
            loadComponent: () => import('@conecta/feature-ide/git-integration/pages/git-conflicts/git-conflicts.component').then(m => m.GitConflictsComponent),
            canActivate: [permissionGuard],
            title: 'titles.gitIntegrationConflicts',
          },
          {
            path: 'integracion-git/pipelines',
            loadComponent: () => import('@conecta/feature-ide/git-integration/pages/git-pipelines/git-pipelines.component').then(m => m.GitPipelinesComponent),
            canActivate: [permissionGuard],
            title: 'titles.gitIntegrationPipelines',
          },
          {
            path: 'integracion-git/historial',
            loadComponent: () => import('@conecta/feature-ide/git-integration/pages/git-history/git-history.component').then(m => m.GitHistoryComponent),
            canActivate: [permissionGuard],
            title: 'titles.gitIntegrationHistory',
          },
        ],
      },
      // Modulo Gobernanza y Gestion de APIs
      {
        path: 'integraciones',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'api-gateway', pathMatch: 'full' },
          {
            path: 'api-gateway',
            loadComponent: () => import('@conecta/feature-api-management/api-gateway/main/main.component').then(m => m.MainComponent),
            canActivate: [permissionGuard],
            children: [
              { path: '', redirectTo: 'proxy-routing', pathMatch: 'full' },
              {
                path: 'proxy-routing',
                loadComponent: () => import('@conecta/feature-api-management/api-gateway/proxy-routing/components/proxy-catalog/proxy-catalog.component').then(m => m.ProxyCatalogComponent),
                canActivate: [permissionGuard],
                title: 'titles.apiProxies',
              },
              {
                path: 'rate-limiting',
                loadComponent: () => import('@conecta/feature-api-management/api-gateway/rate-limiting/components/rate-limiting-catalog/rate-limiting-catalog.component').then(m => m.RateLimitingCatalogComponent),
                canActivate: [permissionGuard],
                title: 'Rate limiting avanzado',
              },
              {
                path: 'threat-protection',
                loadComponent: () => import('@conecta/feature-api-management/api-gateway/threat-protection/components/threat-protection-catalog/threat-protection-catalog.component').then(m => m.ThreatProtectionCatalogComponent),
                canActivate: [permissionGuard],
                title: 'Threat Protection',
              },
              {
                path: 'circuit-breaker',
                loadComponent: () => import('@conecta/feature-api-management/api-gateway/circuit-breaker/components/circuit-breaker-catalog/circuit-breaker-catalog.component').then(m => m.CircuitBreakerCatalogComponent),
                canActivate: [permissionGuard],
                title: 'Circuit Breaker',
              },
              {
                path: 'request-response-transform',
                loadComponent: () => import('../../../../libs/conecta/feature-api-management/src/api-gateway/request-response-transform/components/transform-catalog/transform-catalog.component').then(m => m.TransformCatalogComponent),
                canActivate: [permissionGuard],
                title: 'Request/Response transform',
              },
            ],
          },
        ]
      },
      {
        path: 'integraciones',
        canActivate: [permissionGuard],
        children: [
          { path: '', redirectTo: 'ciclo-vida-apis', pathMatch: 'full' },
          {
            path: 'portal-desarrolladores/:siteId',
            loadComponent: () => import('@conecta/feature-api-management/developers-portal-module/main/portal-module.component').then(m => m.PortalModuleComponent),
            canActivate: [permissionGuard],
            title: 'Portal de desarrolladores',
          },
          {
            path: 'portal-desarrolladores',
            loadComponent: () => import('@conecta/feature-api-management/developers-portal-module/main/portal-module.component').then(m => m.PortalModuleComponent),
            canActivate: [permissionGuard],
            title: 'Portal de desarrolladores',
          },
          {
            path: 'ciclo-vida-apis',
            loadComponent: () => import('@conecta/feature-api-management/api-lifecycle/main/main.component').then(m => m.MainComponent),
            canActivate: [permissionGuard],
            children: [
              { path: '', redirectTo: 'api-first-design', pathMatch: 'full' },
              {
                path: 'api-first-design',
                loadComponent: () => import('@conecta/feature-api-management/api-lifecycle/api-first-design/components/contract-catalog/contract-catalog.component').then(m => m.ContractCatalogComponent),
                canActivate: [permissionGuard],
                title: 'Diseño API-first',
              },
              {
                path: 'version-management',
                loadComponent: () => import('@conecta/feature-api-management/api-lifecycle/version-management/components/version-catalog/version-catalog.component').then(m => m.VersionCatalogComponent),
                canActivate: [permissionGuard],
                title: 'Gestión de versiones',
              },
            ],
          },
          {
            path: 'async-event-driven-apis',
            loadComponent: () => import('@conecta/feature-api-management/asyncapi-event-driven/main/main.component').then(m => m.MainComponent),
            canActivate: [permissionGuard],
            title: 'AsyncAPI y Event-Driven APIs',
            children: [
              { path: '', redirectTo: 'asyncapi-catalog', pathMatch: 'full' },
              {
                path: 'asyncapi-catalog',
                loadComponent: () => import('@conecta/feature-api-management/asyncapi-event-driven/asyncapi-catalog/components/asyncapi-catalog/asyncapi-catalog.component').then(m => m.AsyncApiCatalogComponent),
                canActivate: [permissionGuard],
                title: 'Cat\u00e1logo AsyncAPI',
              },
              {
                path: 'event-schema-registry',
                loadComponent: () => import('@conecta/feature-api-management/asyncapi-event-driven/event-schema-registry/components/registry-page/registry-page.component').then(m => m.RegistryPageComponent),
                canActivate: [permissionGuard],
                title: 'Registro de esquemas',
              },
              {
                path: 'native-cloudevents',
                loadComponent: () => import('@conecta/feature-api-management/asyncapi-event-driven/native-cloudevents/components/cloud-events-page/cloud-events-page.component').then(m => m.CloudEventsPageComponent),
                canActivate: [permissionGuard],
                title: 'CloudEvents nativo',
              },
            ],
          },
        ]
      }
    ],
  },
  // Auth
  {
    path: 'auth',
    loadComponent: () => import('@partnercore/feature-auth-pages/auth/auth.component').then(m => m.AuthComponent),
    canActivate: [redirectAuthenticatedGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/login/login.component-page').then(m => m.LoginPageComponent),
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/forgot-password/forgot-password-page.component').then(m => m.ForgotPasswordPageComponent),
      },
      {
        path: 'reset-password',
        loadComponent: () => import('@partnercore/feature-auth-pages/auth-pages/reset-password/reset-password-page.component').then(m => m.ResetPasswordPageComponent),
      },
    ],
  },
  // Error
  {
    path: '404',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
  {
    path: '**',
    loadComponent: () => import('@partnercore/feature-error-pages/other-page/not-found/not-found.component').then(m => m.NotFoundComponent),
    title: 'titles.notFound',
  },
];
